import { Operator } from "@/types/insights/operators"
import { TenantFeatures } from "./tenantFeatures"

export interface Rule {
	id?: string;
	title: string;
	triggers: Trigger[];
	actions: RuleAction[];
	tenantId: string;
	runOnce: boolean;
}

export interface Trigger {
	id?: string;
	label: string;
	field: string;
	subField: string;
	operator: Operator;
	fieldDataType: number;
	fieldTypeId: string;
	value: string;
}

export interface RuleAction {
	id: string | null;
	key?: string;

	label: string;
	actionType: number;
	settings: { [key: string]: string };
}

export enum ActionType {
	SetValue,
	AddTeam,
	SetFieldRequired,
	SendHttp,
	SendEmail,
	AddField,
	SentToIntegration,
	RemoveField
}

export interface ActionTypeLabel {
	label: string;
	value: number;
	requireFeature?: TenantFeatures;
}

const actionTypeLabels: ActionTypeLabel[] = [
	{
		label: "Set Value",
		value: ActionType.SetValue
	},
	{
		label: "Add Team",
		value: ActionType.AddTeam
	},
	{
		label: "Add Field",
		value: ActionType.AddField
	},
	{
		label: "Set Field Required",
		value: ActionType.SetFieldRequired
	},
	{
		label: "Send HTTPS",
		value: ActionType.SendHttp
	},
	{
		label: "Send Email",
		value: ActionType.SendEmail
	},
	{
		label: "Send to Integration",
		value: ActionType.SentToIntegration
	},
	{
		label: "Remove Field",
		value: ActionType.RemoveField,
		requireFeature: TenantFeatures.RuleActionRemoveField
	}
]

// Defines all the different action setting field
export enum ActionSettingField {
	Field,
	FieldDataType,
	NewValue,
	NewValueLabel,
	FieldId,
	Required,
	TeamId,
	TeamTitle,
	Recipient,
	Subject,
	Section,
	DynamicValue,
	ValueFormat,
	IncludeAttachments,
	URL,
	HttpHeaders,
	Integration
}

interface ActionSetting {
	id: string;
	type: ActionSettingField;
	label: string;
	visible: boolean;
	validation: any ;
}

// Definitions for action settings
const actionSetting: ActionSetting[] = [
	{ id: "field", type: ActionSettingField.Field, label: "Field", visible: true, validation: null },
	{ id: "fieldDataType", type: ActionSettingField.FieldDataType, label: "Data Type", visible: false, validation: null },
	{ id: "newValue", type: ActionSettingField.NewValue, label: "New Value", visible: true, validation: null },
	{ id: "newValueLabel", type: ActionSettingField.NewValueLabel, label: "New value label", visible: false, validation: null },
	{ id: "dynamicValue", type: ActionSettingField.DynamicValue, label: "Dynamic Value", visible: false, validation: null },
	{ id: "valueFormat", type: ActionSettingField.ValueFormat, label: "Value Format", visible: false, validation: null },
	{ id: "fieldId", type: ActionSettingField.FieldId, label: "Field", visible: true, validation: null },
	{ id: "required", type: ActionSettingField.Required, label: "Required", visible: true, validation: null },
	{ id: "includeAttachments", type: ActionSettingField.IncludeAttachments, label: "Include Attachments", visible: true, validation: null },
	{ id: "teamId", type: ActionSettingField.TeamId, label: "Team", visible: true, validation: null },
	{ id: "teamTitle", type: ActionSettingField.TeamTitle, label: "Team Title", visible: false, validation: null },
	{ id: "recipient", type: ActionSettingField.Recipient, label: "Recipient", visible: true, validation: null },
	{ id: "subject", type: ActionSettingField.Subject, label: "Subject", visible: true, validation: null },
	{ id: "section", type: ActionSettingField.Section, label: "Section", visible: true, validation: null },
	{ id: "url", type: ActionSettingField.URL, label: "URL", visible: true, validation: (item: any) => item.toLowerCase().startsWith("https:") ? null : "The supplied URL must be valid 'https:' URL" },
	{ id: "httpHeaders", type: ActionSettingField.HttpHeaders, label: "Headers", visible: true, validation: null },
	{ id: "integration", type: ActionSettingField.Integration, label: "Send to", visible: true, validation: null }
]

// Definition of the fields that should be filled in when an Action Setting Type is used
const actionSettingsForActionType: { [index: number]: ActionSettingField[] } = {}
actionSettingsForActionType[ActionType.SetValue] = [ActionSettingField.Field, ActionSettingField.FieldDataType,
	ActionSettingField.NewValue, ActionSettingField.NewValueLabel, ActionSettingField.DynamicValue,
	ActionSettingField.ValueFormat ]
actionSettingsForActionType[ActionType.SetFieldRequired] = [ActionSettingField.FieldId, ActionSettingField.Required]
actionSettingsForActionType[ActionType.AddTeam] = [ActionSettingField.TeamId, ActionSettingField.TeamTitle]
actionSettingsForActionType[ActionType.SendHttp] = [ActionSettingField.URL, ActionSettingField.HttpHeaders]
actionSettingsForActionType[ActionType.SendEmail] = [ActionSettingField.Recipient, ActionSettingField.Subject, ActionSettingField.IncludeAttachments]
actionSettingsForActionType[ActionType.AddField] = [ActionSettingField.FieldId, ActionSettingField.Required, ActionSettingField.Section]
actionSettingsForActionType[ActionType.SentToIntegration] = [ActionSettingField.Integration]
actionSettingsForActionType[ActionType.RemoveField] = [ActionSettingField.Field]

export { actionTypeLabels, actionSettingsForActionType, actionSetting, ActionSetting }
